/**
 * PortalDocumentReplayPage - Replay page for portal documents
 *
 * Displays the edit replay for documents written in the student portal.
 * Uses the shared ReplayContainer component from @markeroo/replay.
 */

import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { useCallback, useMemo } from "react";
import type { ReplayNavigationState } from "@markeroo/replay";
import PiArrowLeftStroke from "@markeroo/icons/src/stroke/arrows-&-chevrons/PiArrowLeftStroke";
import { useDocumentEditEvents } from "@/api/hooks";
import { ReplayContainer } from "./replay";
import { PageHeader } from "@/components/PageHeader";
import { pageContainer, cardContainer } from "@/styles/variants";
import { buttonVariants } from "@markeroo/theme";
import { Spinner } from "@/components/Spinner";
import { ui } from "@/strings";
import { useSetBreadcrumbs } from "@/hooks/useSetBreadcrumbs";

export function PortalDocumentReplayPage() {
  const { classId, assignmentId, documentId } = useParams<{
    classId: string;
    assignmentId: string;
    documentId: string;
  }>();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  // Fetch events data for replay
  const { data: eventsData, isLoading, isError } = useDocumentEditEvents(
    assignmentId,
    documentId
  );

  // Parse query params for deep-linking (?moment=5 or ?moment=5&edit=2)
  const initialNavigationState = useMemo(() => {
    const momentParam = searchParams.get("moment");
    if (!momentParam) return undefined;

    const moment = parseInt(momentParam, 10);
    if (isNaN(moment)) return undefined;

    const editParam = searchParams.get("edit");
    const edit = editParam ? parseInt(editParam, 10) : null;

    return {
      moment,
      edit: edit !== null && !isNaN(edit) ? edit : null,
    };
  }, [searchParams]);

  // Sync URL when navigation changes (called by ReplayContainer)
  const handleNavigationChange = useCallback(
    (state: ReplayNavigationState) => {
      setSearchParams(
        (prev) => {
          const params = new URLSearchParams(prev);
          params.set("moment", String(state.moment));
          if (state.edit !== null) {
            params.set("edit", String(state.edit));
          } else {
            params.delete("edit");
          }
          return params;
        },
        { replace: true }
      );
    },
    [setSearchParams]
  );

  const assignmentLink = classId && assignmentId
    ? `/classes/${classId}/assignments/${assignmentId}`
    : "/classes";

  useSetBreadcrumbs([
    { label: ui.headers.classes, to: "/classes" },
    { label: ui.labels.assignment, to: assignmentLink },
    { label: ui.editMetrics.editReplay },
  ]);

  const handleBack = () => {
    void navigate(`/classes/${classId}/assignments/${assignmentId}`);
  };

  if (isLoading) {
    return (
      <div className={pageContainer}>
        <PageHeader>
          <PageHeader.Title>
            <button
              type="button"
              onClick={handleBack}
              className="mr-3 rounded-lg p-1 hover:bg-surface-secondary"
              aria-label={ui.actions.back}
              title={ui.actions.back}
            >
              <PiArrowLeftStroke size={20} className="text-theme-muted" aria-hidden="true" />
            </button>
            {ui.editMetrics.editReplay}
          </PageHeader.Title>
        </PageHeader>
        <section className={cardContainer}>
          <div className="flex items-center justify-center py-16">
            <Spinner size="lg" className="text-primary" />
            <span className="ml-3 text-sm text-theme-muted">
              {ui.loading.default}
            </span>
          </div>
        </section>
      </div>
    );
  }

  if (isError || !eventsData) {
    return (
      <div className={pageContainer}>
        <PageHeader>
          <PageHeader.Title>
            <button
              type="button"
              onClick={handleBack}
              className="mr-3 rounded-lg p-1 hover:bg-surface-secondary"
              aria-label={ui.actions.back}
              title={ui.actions.back}
            >
              <PiArrowLeftStroke size={20} className="text-theme-muted" aria-hidden="true" />
            </button>
            {ui.editMetrics.editReplay}
          </PageHeader.Title>
        </PageHeader>
        <section className={cardContainer}>
          <div className="rounded-xl border border-semantic-error bg-semantic-error-bg p-6 text-center">
            <p className="text-semantic-error">{ui.editMetrics.loadEventsFailed}</p>
            <button
              type="button"
              onClick={handleBack}
              className={`${buttonVariants.secondary} mt-4`}
            >
              <PiArrowLeftStroke size={16} />
              {ui.actions.back}
            </button>
          </div>
        </section>
      </div>
    );
  }

  // Use a viewport-fitting layout for the replay view
  // Note: Header is ~66px, so we use calc(100vh - 66px) to fit exactly below it
  return (
    <div className="mx-auto flex h-[calc(100vh-66px)] max-h-[calc(100vh-66px)] max-w-page flex-col gap-4 overflow-auto px-6 pt-4 pb-4 lg:px-8">
      <PageHeader>
        <PageHeader.Title>
          <button
            type="button"
            onClick={handleBack}
            className="mr-3 rounded-lg p-1 hover:bg-surface-secondary"
            aria-label={ui.actions.back}
            title={ui.actions.back}
          >
            <PiArrowLeftStroke size={20} className="text-theme-muted" aria-hidden="true" />
          </button>
          {ui.editMetrics.editReplay}
        </PageHeader.Title>
      </PageHeader>

      {/* Writing Replay - fills remaining viewport */}
      <section className="flex min-h-0 flex-1 flex-col gap-4 overflow-hidden rounded-2xl bg-surface-card p-4 shadow-card">
        <div className="flex-shrink-0">
          <h2 className="text-lg font-semibold text-theme-primary">
            {ui.aiDetector.writingReplay}
          </h2>
          <p className="mt-1 text-sm text-theme-muted">
            {ui.aiDetector.writingReplayDescription}
          </p>
          <div className="mt-3 flex items-center gap-4 text-sm text-theme-muted">
            <span>
              {eventsData.total.toLocaleString()} {ui.editMetrics.editEvents}
            </span>
          </div>
        </div>

        <ReplayContainer
          events={eventsData.events}
          initialText={eventsData.initialText}
          className="min-h-0 flex-1"
          initialNavigationState={initialNavigationState}
          onNavigationChange={handleNavigationChange}
        />
      </section>
    </div>
  );
}
